import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, AlertTriangle, DollarSign, Shield, Globe, Clock, CheckCircle, Zap, ArrowRight, Eye, Lock, Monitor } from 'lucide-react';

const SMITHERS_ARMORED = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/vkr6m8a6_image_9db183aa-9389-4fc5-a837-5d011fd63d94.png';
const HUD_FRAME = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/57subxmn_image_35d870a4-4809-4734-9d12-5a644722d83a.png';
const INITIALIZING_BG = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/3zu5cj9g_image_15e0a3d5-9454-4eef-9f96-5546bab0bc2e.png';
const DIGITAL_HUB_BG = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/szf5gp0i_image_619ce18b-e0d8-4d1d-958a-d29af05c397f.png';
const SMITHERS_VIDEO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/4uhgcai8_grok-video-b240d936-7642-40ea-9f30-86b87a3513e2%20%285%29.mp4';

const MOCK_VIOLATION_SETS = [
  [
    { id: 1, platform: 'Instagram', url: 'instagram.com/p/xxx...', type: 'Image repost', match: 97, revenue: '$340', detected: '2 hours ago' },
    { id: 2, platform: 'Twitter/X', url: 'x.com/status/xxx...', type: 'Video embed', match: 94, revenue: '$520', detected: '5 hours ago' },
    { id: 3, platform: 'TikTok', url: 'tiktok.com/@user/xxx...', type: 'Video repost', match: 91, revenue: '$1,200', detected: '1 day ago' },
    { id: 4, platform: 'Reddit', url: 'reddit.com/r/xxx...', type: 'Image repost', match: 96, revenue: '$180', detected: '1 day ago' },
    { id: 5, platform: 'Telegram', url: 'Telegram channel', type: 'Bulk distribution', match: 89, revenue: '$3,400', detected: '2 days ago' },
    { id: 6, platform: 'OnlyLeaks', url: 'External site', type: 'Full content mirror', match: 99, revenue: '$4,200', detected: '3 days ago' },
    { id: 7, platform: 'Pinterest', url: 'pinterest.com/pin/xxx...', type: 'Image repost', match: 88, revenue: '$120', detected: '4 days ago' },
    { id: 8, platform: 'Facebook', url: 'facebook.com/xxx...', type: 'Shared post', match: 93, revenue: '$290', detected: '5 days ago' },
  ],
  [
    { id: 1, platform: 'YouTube', url: 'youtube.com/watch?v=xxx', type: 'Full re-upload', match: 98, revenue: '$2,800', detected: '1 hour ago' },
    { id: 2, platform: 'TikTok', url: 'tiktok.com/@remix_king/...', type: 'Audio rip', match: 96, revenue: '$1,600', detected: '3 hours ago' },
    { id: 3, platform: 'SoundCloud', url: 'soundcloud.com/user/...', type: 'Beat theft', match: 99, revenue: '$890', detected: '6 hours ago' },
    { id: 4, platform: 'Spotify', url: 'open.spotify.com/track/...', type: 'Unlicensed sample', match: 87, revenue: '$4,100', detected: '1 day ago' },
    { id: 5, platform: 'Instagram', url: 'instagram.com/reels/...', type: 'Reel with audio', match: 92, revenue: '$560', detected: '1 day ago' },
    { id: 6, platform: 'Snapchat', url: 'Spotlight post', type: 'Audio use', match: 85, revenue: '$220', detected: '2 days ago' },
    { id: 7, platform: 'Triller', url: 'triller.co/@user/...', type: 'Video sync', match: 91, revenue: '$180', detected: '3 days ago' },
    { id: 8, platform: 'Twitter/X', url: 'x.com/status/xxx...', type: 'Embedded clip', match: 94, revenue: '$740', detected: '4 days ago' },
  ],
  [
    { id: 1, platform: 'Telegram', url: 'Piracy channel', type: 'Full catalog leak', match: 100, revenue: '$12,400', detected: '30 min ago' },
    { id: 2, platform: 'Discord', url: 'discord.gg/xxx', type: 'File sharing', match: 98, revenue: '$3,200', detected: '2 hours ago' },
    { id: 3, platform: 'Reddit', url: 'reddit.com/r/piracy/...', type: 'Download link', match: 97, revenue: '$1,800', detected: '4 hours ago' },
    { id: 4, platform: 'YouTube', url: 'youtube.com/watch?v=...', type: 'Commentary use', match: 78, revenue: '$420', detected: '8 hours ago' },
    { id: 5, platform: 'TikTok', url: 'tiktok.com/@viral/...', type: 'Sound reuse', match: 93, revenue: '$2,100', detected: '1 day ago' },
    { id: 6, platform: 'Instagram', url: 'instagram.com/p/...', type: 'Repost no credit', match: 95, revenue: '$380', detected: '2 days ago' },
    { id: 7, platform: 'Twitter/X', url: 'x.com/status/...', type: 'Thread embed', match: 89, revenue: '$290', detected: '3 days ago' },
    { id: 8, platform: 'Facebook', url: 'facebook.com/groups/...', type: 'Group share', match: 91, revenue: '$510', detected: '4 days ago' },
  ],
];

const PLATFORM_COLORS = {
  YouTube: '#FF0000',
  TikTok: '#00F0FF',
  Instagram: '#E4405F',
  'Twitter/X': '#1DA1F2',
  Facebook: '#1877F2',
  Reddit: '#FF4500',
  SoundCloud: '#FF5500',
  Telegram: '#0088CC',
  Snapchat: '#FFFC00',
  Pinterest: '#BD081C',
  Discord: '#5865F2',
  Spotify: '#1DB954',
  Triller: '#FF00FF',
  OnlyLeaks: '#DC2626',
};

const SCAN_PHASES = [
  'Initializing SMITHERS AI...',
  'Indexing your content library...',
  'Scanning social media platforms...',
  'Crawling media databases...',
  'Analyzing visual fingerprints...',
  'Cross-referencing results...',
  'Calculating estimated revenue...',
  'Compiling report...',
];

export default function ScanDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [scanProgress, setScanProgress] = useState(0);
  const [scanPhaseText, setScanPhaseText] = useState('');
  const [violations, setViolations] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [smithersVisible, setSmithersVisible] = useState(false);

  // Activation sequence states
  const [activationPhase, setActivationPhase] = useState('idle'); // idle, loading, flashing, done
  const [flashCount, setFlashCount] = useState(0);
  const [activationProgress, setActivationProgress] = useState(0);

  useEffect(() => {
    if (!user) navigate('/auth');
  }, [user, navigate]);

  // Skip video to last 10 seconds when scanning starts
  useEffect(() => {
    if (phase === 'scanning' && videoRef.current) {
      const video = videoRef.current;
      const handleLoaded = () => {
        if (video.duration > 10) {
          video.currentTime = video.duration - 10;
        }
        video.play().catch(() => {});
      };
      if (video.readyState >= 1) {
        handleLoaded();
      } else {
        video.addEventListener('loadedmetadata', handleLoaded, { once: true });
      }
    }
  }, [phase]);

  const startScan = useCallback(() => {
    setPhase('scanning');
    setScanProgress(0);
    setViolations([]);
    setSmithersVisible(false);
    setTimeout(() => setSmithersVisible(true), 800);

    // Pick a random violation set so scans don't repeat
    const setIndex = Math.floor(Math.random() * MOCK_VIOLATION_SETS.length);
    const selectedViolations = MOCK_VIOLATION_SETS[setIndex];
    const revenue = selectedViolations.reduce((s, v) => s + parseInt(v.revenue.replace(/[$,]/g, '')), 0);

    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 4 + 1;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setTimeout(() => {
          setPhase('results');
          setViolations(selectedViolations);
          setTotalRevenue(revenue);
        }, 600);
      }
      setScanProgress(Math.min(progress, 100));
      const phaseIndex = Math.min(Math.floor((progress / 100) * SCAN_PHASES.length), SCAN_PHASES.length - 1);
      setScanPhaseText(SCAN_PHASES[phaseIndex]);
    }, 200);
    return () => clearInterval(interval);
  }, []);

  // Activation sequence
  const handleActivate = () => {
    setActivationPhase('loading');
    setActivationProgress(0);

    // Green loading bar
    let prog = 0;
    const loadInterval = setInterval(() => {
      prog += Math.random() * 8 + 3;
      if (prog >= 100) {
        prog = 100;
        clearInterval(loadInterval);
        setActivationProgress(100);
        // Start flashing
        setTimeout(() => {
          setActivationPhase('flashing');
          let count = 0;
          const flashInterval = setInterval(() => {
            count++;
            setFlashCount(count);
            if (count >= 6) {
              clearInterval(flashInterval);
              setTimeout(() => setActivationPhase('done'), 400);
            }
          }, 300);
        }, 400);
      }
      setActivationProgress(Math.min(prog, 100));
    }, 80);
  };

  return (
    <div className="min-h-screen bg-texture" data-testid="scan-dashboard">

      {/* IDLE STATE */}
      {phase === 'idle' && (
        <div className="relative min-h-[calc(100vh-64px)] flex items-center justify-center overflow-hidden" data-testid="scan-idle">
          <div className="absolute inset-0">
            <img src={HUD_FRAME} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[--ch-bg-primary] via-[rgba(13,13,13,0.6)] to-[rgba(13,13,13,0.8)]" />
          </div>
          <div className="relative z-10 flex flex-col items-center text-center px-4 animate-fade-in-up">
            <div className="relative mb-8">
              <div className="w-32 h-32 rounded-full border-2 border-[--ch-cyan] overflow-hidden shadow-[0_0_30px_rgba(0,240,255,0.2)]">
                <img src={SMITHERS_ARMORED} alt="SMITHERS" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[--ch-success] flex items-center justify-center shadow-[0_0_8px_rgba(34,197,94,0.5)]">
                <CheckCircle size={16} className="text-black" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-center mb-3" style={{ fontFamily: 'Orbitron' }} data-testid="scan-title">
              <span className="text-white">Ready to </span><span className="neon-cyan">Scan</span>
            </h1>
            <p className="text-base text-[--ch-text-secondary] text-center max-w-md mb-2">
              SMITHERS will search the internet for unauthorized uses of your content and calculate your uncollected revenue.
            </p>
            <p className="text-sm text-[--ch-text-secondary] mb-8">
              Connected as <span className="text-white font-semibold">{user?.username || user?.email}</span>
            </p>
            <button data-testid="start-scan-btn" onClick={startScan}
              className="btn-neon px-10 py-4 rounded-full text-sm font-semibold flex items-center gap-2 animate-pulse-glow">
              <Search size={18} /> Scan My Content
            </button>
            <p className="text-sm text-[--ch-text-secondary] mt-4">Takes approximately 30 seconds</p>
          </div>
        </div>
      )}

      {/* SCANNING STATE */}
      {phase === 'scanning' && (
        <div className="relative min-h-[calc(100vh-64px)] flex items-center justify-center overflow-hidden" data-testid="scan-scanning">
          <video ref={videoRef} autoPlay loop muted playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-20">
            <source src={SMITHERS_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0">
            <img src={INITIALIZING_BG} alt="" className="w-full h-full object-cover opacity-15" />
            <div className="absolute inset-0 bg-gradient-to-t from-[--ch-bg-primary] via-[rgba(13,13,13,0.5)] to-[rgba(13,13,13,0.7)]" />
          </div>
          <div className="relative z-10 flex flex-col items-center text-center px-4">
            <div className={`relative mb-8 transition-all duration-1000 ${smithersVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-90'}`}>
              <div className="w-40 h-56 rounded-2xl overflow-hidden border border-[--ch-cyan] shadow-[0_0_40px_rgba(0,240,255,0.25)]">
                <img src={SMITHERS_ARMORED} alt="SMITHERS" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -inset-4 rounded-3xl border border-[--ch-cyan] animate-ping opacity-20" />
              <div className="absolute -inset-8 rounded-3xl border border-[--ch-cyan] animate-ping opacity-10" style={{ animationDelay: '0.5s' }} />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Orbitron' }}>
              <span className="neon-cyan">Scanning</span>
            </h2>
            <p className="text-sm text-[--ch-text-secondary] mb-6 text-center">{scanPhaseText}</p>
            <div className="w-80 max-w-full">
              <div className="h-2 bg-[--ch-bg-secondary] rounded-full overflow-hidden mb-2">
                <div className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${scanProgress}%`, background: 'linear-gradient(90deg, var(--ch-cyan), var(--ch-magenta))', boxShadow: '0 0 12px rgba(0,240,255,0.5)' }} />
              </div>
              <p className="text-sm text-[--ch-text-secondary] text-center font-mono">{Math.round(scanProgress)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* RESULTS STATE */}
      {phase === 'results' && (
        <div className="relative" data-testid="scan-results">
          <div className="absolute inset-0 pointer-events-none">
            <img src={DIGITAL_HUB_BG} alt="" className="w-full h-full object-cover opacity-5" />
          </div>
          <div className="relative z-10 max-w-6xl mx-auto px-4 py-8 animate-fade-in-up">
            {/* Header summary - BIGGER violation count */}
            <div className="ch-card p-6 mb-6 relative overflow-hidden" data-testid="results-summary">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[--ch-cyan] rounded-full opacity-[0.03] blur-[80px]" />
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[--ch-cyan] flex-shrink-0 shadow-[0_0_15px_rgba(0,240,255,0.2)]">
                  <img src={SMITHERS_ARMORED} alt="SMITHERS" className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[--ch-text-secondary] mb-1">SMITHERS Report</p>
                  <h2 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: 'Orbitron' }} data-testid="results-headline">
                    I found <span className="neon-magenta text-3xl sm:text-4xl">{violations.length} unauthorized uses</span> of your content.
                  </h2>
                  <p className="text-xl mt-2">
                    Estimated uncollected revenue: <span className="neon-cyan font-bold text-3xl">${totalRevenue.toLocaleString()}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Violations Found', value: violations.length, icon: AlertTriangle, color: 'magenta' },
                { label: 'Est. Revenue', value: `$${totalRevenue.toLocaleString()}`, icon: DollarSign, color: 'cyan' },
                { label: 'Platforms Scanned', value: '24', icon: Globe, color: 'purple' },
                { label: 'Match Accuracy', value: '94%', icon: Eye, color: 'cyan' },
              ].map((stat, i) => (
                <div key={i} className="ch-card p-4" data-testid={`result-stat-${i}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
                    stat.color === 'cyan' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' :
                    stat.color === 'magenta' ? 'bg-[rgba(255,0,229,0.1)] text-[--ch-magenta]' :
                    'bg-[rgba(139,92,246,0.1)] text-[--ch-purple]'
                  }`}>
                    <stat.icon size={16} />
                  </div>
                  <p className="text-sm text-[--ch-text-secondary]">{stat.label}</p>
                  <p className="text-xl font-black mt-0.5">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* ORACLE ENGINE WAR ROOM — Platform Monitors */}
            <div className="mb-6" data-testid="oracle-war-room">
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
                <Monitor size={14} className="text-[--ch-cyan]" /> ORACLE ENGINE — Platform Triage
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {(() => {
                  // Group violations by platform
                  const platforms = {};
                  violations.forEach(v => {
                    if (!platforms[v.platform]) {
                      platforms[v.platform] = { count: 0, revenue: 0, maxMatch: 0 };
                    }
                    platforms[v.platform].count++;
                    platforms[v.platform].revenue += parseInt(v.revenue.replace(/[$,]/g, ''));
                    platforms[v.platform].maxMatch = Math.max(platforms[v.platform].maxMatch, v.match);
                  });
                  // Add idle platforms
                  ['YouTube', 'TikTok', 'Instagram', 'Twitter/X', 'SoundCloud', 'Snapchat', 'Facebook', 'Telegram'].forEach(p => {
                    if (!platforms[p]) platforms[p] = { count: 0, revenue: 0, maxMatch: 0 };
                  });
                  return Object.entries(platforms).map(([platform, data]) => {
                    const color = PLATFORM_COLORS[platform] || '#888';
                    const isActive = data.count > 0;
                    return (
                      <div key={platform} className="ch-card p-3 relative overflow-hidden" data-testid={`monitor-${platform}`}
                        style={isActive ? { borderColor: `${color}30`, boxShadow: `0 0 12px ${color}08` } : {}}>
                        {/* Monitor header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full" style={{
                              background: isActive ? color : '#333',
                              boxShadow: isActive ? `0 0 6px ${color}60` : 'none'
                            }} />
                            <span className="text-[10px] font-bold" style={{ color: isActive ? color : '#555' }}>
                              {platform}
                            </span>
                          </div>
                          {isActive && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                              style={{ background: `${color}15`, color }}>
                              {data.count} HIT{data.count > 1 ? 'S' : ''}
                            </span>
                          )}
                        </div>
                        {/* Progress bar */}
                        <div className="h-1 bg-[rgba(30,30,30,0.8)] rounded-full overflow-hidden mb-2">
                          <div className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: isActive ? `${Math.min(data.maxMatch, 100)}%` : '0%',
                              background: color,
                              opacity: 0.7,
                            }} />
                        </div>
                        {isActive ? (
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold" style={{ color }}>${data.revenue.toLocaleString()}</span>
                            <span className="text-[9px] text-[--ch-text-secondary]">{data.maxMatch}% match</span>
                          </div>
                        ) : (
                          <p className="text-[9px] text-[--ch-text-secondary]">No violations detected</p>
                        )}
                        {/* Subtle scan line for active */}
                        {isActive && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${color}30, transparent)` }} />
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* ACTIVATION CTA - with animation sequence */}
            {activationPhase === 'idle' && (
              <div className="ch-card p-6 mb-6 text-center neon-border-cyan" data-testid="activate-cta">
                <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Orbitron' }}>
                  <span className="neon-cyan">Activate SMITHERS</span>
                </h3>
                <p className="text-sm text-[--ch-text-secondary] mb-4 max-w-md mx-auto">
                  Let SMITHERS send automated takedown notices and recover your owed royalties.
                </p>
                <button data-testid="activate-smithers-btn" onClick={handleActivate}
                  className="btn-neon px-8 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2">
                  <Zap size={16} /> Activate SMITHERS <ArrowRight size={16} />
                </button>
              </div>
            )}

            {/* Loading phase - green progress bar */}
            {activationPhase === 'loading' && (
              <div className="ch-card p-8 mb-6 text-center" data-testid="activation-loading"
                style={{ borderColor: 'var(--ch-success)', boxShadow: '0 0 20px rgba(34,197,94,0.15)' }}>
                <div className="w-12 h-12 rounded-full bg-[rgba(34,197,94,0.1)] flex items-center justify-center mx-auto mb-4">
                  <Zap size={24} className="text-[--ch-success] animate-pulse" />
                </div>
                <h3 className="text-lg font-bold text-[--ch-success] mb-3" style={{ fontFamily: 'Orbitron' }}>
                  INITIALIZING ENFORCEMENT
                </h3>
                {/* Green loading bar */}
                <div className="w-64 max-w-full mx-auto">
                  <div className="h-2.5 bg-[--ch-bg-secondary] rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full transition-all duration-150"
                      style={{
                        width: `${activationProgress}%`,
                        background: '#22C55E',
                        boxShadow: '0 0 15px rgba(34,197,94,0.6), 0 0 30px rgba(34,197,94,0.3)',
                      }} />
                  </div>
                  <p className="text-sm text-[--ch-success] font-mono">{Math.round(activationProgress)}%</p>
                </div>
              </div>
            )}

            {/* Flashing ACTIVATED phase */}
            {activationPhase === 'flashing' && (
              <div className="ch-card p-8 mb-6 text-center relative overflow-hidden" data-testid="activation-flashing"
                style={{ borderColor: 'var(--ch-success)', boxShadow: '0 0 30px rgba(34,197,94,0.25)' }}>
                {/* Green screen flash overlay */}
                <div className="absolute inset-0 transition-opacity duration-150"
                  style={{
                    background: 'rgba(34,197,94,0.08)',
                    opacity: flashCount % 2 === 0 ? 1 : 0,
                  }} />
                <div className="relative z-10">
                  <h2 className="font-black text-[--ch-success] mb-1"
                    style={{
                      fontFamily: 'Orbitron',
                      fontSize: flashCount % 2 === 0 ? '2.5rem' : '2rem',
                      textShadow: '0 0 20px rgba(34,197,94,0.8), 0 0 40px rgba(34,197,94,0.4), 0 0 60px rgba(34,197,94,0.2)',
                      transition: 'all 0.15s ease',
                    }}>
                    ACTIVATED
                  </h2>
                </div>
              </div>
            )}

            {/* Final activated state */}
            {activationPhase === 'done' && (
              <div className="ch-card p-6 mb-6 text-center animate-fade-in-up" data-testid="activated-confirmation"
                style={{ borderColor: 'var(--ch-success)', boxShadow: '0 0 15px rgba(34,197,94,0.15)' }}>
                <div className="w-14 h-14 rounded-full bg-[rgba(34,197,94,0.15)] flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={28} className="text-[--ch-success]" />
                </div>
                <h3 className="text-xl font-bold text-[--ch-success] mb-1" style={{ fontFamily: 'Orbitron', textShadow: '0 0 10px rgba(34,197,94,0.5)' }}>
                  ACTIVATED
                </h3>
                <p className="text-base text-[--ch-text-secondary]">
                  Enforcement in progress. You'll receive updates as actions are taken.
                </p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <div className="w-2 h-2 rounded-full bg-[--ch-success] animate-pulse shadow-[0_0_6px_rgba(34,197,94,0.6)]" />
                  <span className="text-sm text-[--ch-success] font-semibold">SMITHERS is working</span>
                </div>
              </div>
            )}

            {/* Violations table */}
            <div className="ch-card overflow-hidden" data-testid="violations-table">
              <div className="p-4 border-b border-[--ch-border] flex items-center justify-between">
                <h3 className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
                  <AlertTriangle size={14} className="text-[--ch-magenta]" /> Detected Violations
                </h3>
                <span className="text-sm text-[--ch-text-secondary]">{violations.length} total</span>
              </div>
              <div className="divide-y divide-[--ch-border]">
                {violations.map((v, idx) => {
                  const isLocked = !user?.subscription_tier && idx >= 2;
                  return (
                    <div key={v.id} className={`p-4 flex items-center gap-4 transition-colors relative ${isLocked ? '' : 'hover:bg-[--ch-bg-card-hover]'}`} data-testid={`violation-${v.id}`}>
                      {isLocked && (
                        <div className="absolute inset-0 z-10 backdrop-blur-[6px] bg-[rgba(13,13,13,0.6)]" />
                      )}
                      <div className="w-10 h-10 rounded-lg bg-[rgba(255,0,229,0.1)] flex items-center justify-center flex-shrink-0">
                        <Globe size={18} className="text-[--ch-magenta]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-semibold">{v.platform}</p>
                          <span className="text-xs px-2 py-0.5 rounded bg-[rgba(255,0,229,0.1)] text-[--ch-magenta] font-bold">{v.type}</span>
                        </div>
                        <p className="text-sm text-[--ch-text-secondary] truncate">{v.url}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-[--ch-cyan]">{v.revenue}</p>
                        <p className="text-xs text-[--ch-text-secondary]">{v.match}% match</p>
                      </div>
                      <div className="text-right flex-shrink-0 hidden sm:block">
                        <p className="text-xs text-[--ch-text-secondary] flex items-center gap-1">
                          <Clock size={10} /> {v.detected}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* CASH-E Paywall CTA — shown when user has no subscription */}
              {!user?.subscription_tier && violations.length > 2 && (
                <div className="p-6 text-center border-t border-[--ch-border] relative" data-testid="cashe-paywall">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
                    style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                    <Lock size={12} className="text-[--ch-warning]" />
                    <span className="font-label text-[--ch-warning]">FOUND CASH-E</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Orbitron' }}>
                    <span className="text-white">{violations.length - 2} more violations</span>{' '}
                    <span className="neon-cyan">hidden</span>
                  </h3>
                  <p className="text-sm text-[--ch-text-secondary] mb-4 max-w-md mx-auto">
                    Upgrade to unlock all scan results, enforcement tools, and start recovering your revenue.
                  </p>
                  <button
                    data-testid="cashe-upgrade-btn"
                    onClick={() => navigate('/subscribe')}
                    className="btn-neon px-8 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                  >
                    <DollarSign size={14} /> Unlock Full Results <ArrowRight size={14} />
                  </button>
                  <p className="text-xs text-[--ch-text-secondary] mt-3">Starting at $29.99/month with Scout</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
